import React, { useState, useEffect, useRef, useCallback } from "react";
import { useI18n } from "../../hooks/useI18n";
import EngagementMasterTab from "./tabs/EngagementMasterTab";
import ServiceDetailsTab from "./tabs/ServiceDetailsTab";
import HistoryTab from "./tabs/HistoryTab";
import "./EngagementMaster.scss";
import { Button } from "../CommonUIComponents/Button/Button";
import { RightArrowIcon } from "../../icons";
import { EditIcon } from "../../icons/editIcon";
import SplitButton from "../CommonUIComponents/SplitButton/SplitButton";
import { MotifDropdown, MotifDropdownItem } from "@ey-xd/motif-react";
import { CancelOrderModal } from "../CancelOrderModal/CancelOrderModal";
import { useDispatch, useSelector } from "react-redux";
import { AssignEngagementManagerModal } from "../CommonUIComponents/AssignEngagementManagerModal/AssignEngagementManagerModal";
import { ConfirmationModal } from "../CommonUIComponents/ConfirmationModal/ConfimationModal";
import { EditServiceDetailsModal } from "./EditServiceDetailsModal";
import { AOAEditServiceDetailsModal } from "./AOAEditServiceDetailsModal";
import {
  assignEM,
  fetchOrderDetails,
} from "../../store/slices/resourceManagerSlice";
import { upsertServiceDetails } from "../../store/slices/engagementManagerSlice";
import { localDateInputToUtcIso } from "../../util/dateUtil";
import {
  updateNextStatus,
  clearStatusUpdate,
} from "../../store/slices/stagesSlice";
import { ToastMessage } from "../CommonUIComponents/ToastMessage/ToastMessage";
import {
  isEngagementManager as checkIsEngagementManager,
  isResourceManager as checkIsResourceManager,
} from "../../util/roleHelpers";

// Pulls the most user-friendly message out of various backend error shapes
// (string, { message }, { title, detail }, ASP.NET ModelState { errors: { Field: [..] } }, etc.)
function extractBackendErrorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;

  if (payload.errors && typeof payload.errors === "object") {
    const collected = [];
    for (const key of Object.keys(payload.errors)) {
      const val = payload.errors[key];
      if (Array.isArray(val)) collected.push(...val);
      else if (typeof val === "string") collected.push(val);
    }
    if (collected.length > 0) return collected.join(" ");
  }

  return (
    payload.message ||
    payload.detail ||
    payload.title ||
    payload.error ||
    fallback
  );
}

const EngagementMaster = ({
  data = {},
  currentStageId,
  isTerminal = false,
  isDisabled: isDisabledProp,
  isCurrentUserDelegate = false,
  domain,
  onAssignSuccess,
  viewContext,
}) => {
  const isDisabled = isDisabledProp ?? isTerminal;
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState("engagement-master");
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalError, setEditModalError] = useState("");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showNextStatusModal, setShowNextStatusModal] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [pendingNextStatus, setPendingNextStatus] = useState(null);

  const roleData = useSelector((state) => state.landing.roleData);
  const { assignLoading } = useSelector((state) => state.resourceManager);
  const {
    isStatusUpdating,
    statusUpdateError,
    statusUpdateData,
    stagesNamesData,
  } = useSelector((state) => state.stages);
  const { upsertServiceDetailsLoading } = useSelector(
    (state) => state.engagementManager,
  );
  const currentUser = useSelector((state) => state.user.user);
  const userEmail = currentUser?.username || currentUser?.email || "";
  const userIsResourceManager = checkIsResourceManager(roleData?.role);
  const userIsEngagementManager = checkIsEngagementManager(roleData?.role);

  // Prefer explicit viewContext from parent (ViewDetailsPage). Fall back to
  // role-based logic when the prop isn't provided (e.g. older callers).
  const resolvedViewContext =
    viewContext ??
    (userIsResourceManager
      ? "resource"
      : userIsEngagementManager
        ? "engagement"
        : null);
  const isEngagementView = resolvedViewContext === "engagement";

  const toastTimerRef = useRef(null);
  const showAutoToast = useCallback((message, variant, durationMs = 2000) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    toastTimerRef.current = setTimeout(() => setShowToast(false), durationMs);
  }, []);

  useEffect(() => {
    if (statusUpdateData) {
      const msg = statusUpdateData?.message || "Status updated successfully!";
      const currentRequestId = Number(data?.requestId || data?.requestID);
      dispatch(clearStatusUpdate());
      showAutoToast(msg, "success");
      if (currentRequestId) {
        dispatch(
          fetchOrderDetails({
            requestId: currentRequestId,
            domain,
            engagementManagerMailId: userEmail || null,
          }),
        );
      }
    }
  }, [statusUpdateData]);

  useEffect(() => {
    if (statusUpdateError) {
      const msg =
        typeof statusUpdateError === "string"
          ? statusUpdateError
          : statusUpdateError?.message || "Failed to update status";
      dispatch(clearStatusUpdate());
      showAutoToast(msg, "error");
    }
  }, [statusUpdateError]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Stage mapping for "Next Status" actions
  // Stage 8 -> 9 (Second Level Review Completed) or Stage 8 -> 10 (Request Completed)
  const stageMapping = {
    "Second Level Review Completed": 9,
    "Request Completed": 10,
  };

  const getStageName = (stageOrder) => {
    const stage = Array.isArray(stagesNamesData)
      ? stagesNamesData.find((s) => s.stageOrder === stageOrder)
      : null;
    return stage?.stageName || `Stage ${stageOrder}`;
  };

  const isAOA = String(domain).toLowerCase().includes("aoa");
  const isCancelServiceRequestDisabled = isDisabled || isCurrentUserDelegate;

  const tabs = [
    {
      id: "engagement-master",
      label: t("view_order_page_engagementMaster", {
        defaultValue: "Engagement Master",
      }),
      component: EngagementMasterTab,
    },
    {
      id: "service-details",
      label: t("view_order_page_serviceDetails", {
        defaultValue: "Service Details",
      }),
      component: ServiceDetailsTab,
    },
    {
      id: "history",
      label: t("view_order_page_history", { defaultValue: "History" }),
      component: HistoryTab,
    },
  ];

  const renderTabContent = () => {
    const activeTabData = tabs.find((tab) => tab.id === activeTab);
    if (!activeTabData) return null;

    const TabComponent = activeTabData.component;
    if (activeTabData.id === "service-details") {
      return (
        <TabComponent
          data={data}
          domain={domain}
          currentStageId={currentStageId}
          isTerminal={isTerminal}
          isDisabled={isDisabled}
          viewContext={resolvedViewContext}
        />
      );
    }
    return <TabComponent data={data} domain={domain} />;
  };

  const handleOpenCancelOrderModal = () => {
    setShowCancelOrderModal(true);
  };

  const handleCloseCancelOrderModal = () => {
    setShowCancelOrderModal(false);
  };

  const handleConfirmCancelOrder = ({ orderId, comment }) => {
    const currentRequestId = Number(data?.requestId || data?.requestID);
    const currentUserEmail = roleData?.userEmail || "";

    if (!currentRequestId || !currentUserEmail) {
      console.error("Missing required data: requestId or email");
      setShowCancelOrderModal(false);
      return;
    }

    dispatch(
      updateNextStatus({
        requestId: currentRequestId,
        newStageId: 10,
        emailId: currentUserEmail,
        comment: comment || "",
        domain,
      }),
    );

    setShowCancelOrderModal(false);
  };

  const handleAssign = async ({ email, firstName, lastName }) => {
    const currentRequestId = Number(data?.requestId || data?.requestID);
    if (!currentRequestId) return;

    const result = await dispatch(
      assignEM({
        requestIds: [currentRequestId],
        engagementManagerEmail: email,
        firstName,
        lastName,
        actingUserEmail: userEmail,
        domain,
      }),
    );

    if (!result.error) {
      setAssignError("");
      setShowAssignModal(false);
      if (onAssignSuccess) {
        onAssignSuccess();
      } else {
        showAutoToast("Engagement Manager changed successfully!", "success");
      }
    } else {
      const errMsg =
        typeof result.payload === "string"
          ? result.payload
          : result.payload?.message || "Failed to change Engagement Manager";
      setAssignError(errMsg);
    }
  };

  const handleNextStatus = (statusLabel) => {
    setPendingNextStatus({ type: "split", label: statusLabel });
    setShowNextStatusModal(true);
  };

  const handleDirectNextStatus = () => {
    if (currentStageId === 7 || currentStageId === 8) {
      setPendingNextStatus({ type: "direct" });
      setShowNextStatusModal(true);
      return;
    }

    const currentRequestId = Number(data?.requestId || data?.requestID);
    const currentUserEmail = roleData?.userEmail || "";

    if (!currentRequestId || !currentUserEmail) {
      console.error("Missing required data: requestId or email");
      return;
    }

    const nextStageId = currentStageId + 1;

    dispatch(
      updateNextStatus({
        requestId: currentRequestId,
        newStageId: nextStageId,
        emailId: currentUserEmail,
        comment: "",
        domain,
      }),
    );
  };

  const handleConfirmNextStatus = () => {
    const currentRequestId = Number(data?.requestId || data?.requestID);
    const currentUserEmail = roleData?.userEmail || "";

    if (!currentRequestId || !currentUserEmail) {
      console.error("Missing required data: requestId or email");
      setShowNextStatusModal(false);
      setPendingNextStatus(null);
      return;
    }

    let nextStageId;
    if (pendingNextStatus?.type === "split") {
      nextStageId = stageMapping[pendingNextStatus.label] - 1;
      if (!nextStageId) {
        console.error(`Unknown status: ${pendingNextStatus.label}`);
        setShowNextStatusModal(false);
        setPendingNextStatus(null);
        return;
      }
    } else {
      nextStageId = currentStageId + 1;
    }

    dispatch(
      updateNextStatus({
        requestId: currentRequestId,
        newStageId: nextStageId,
        emailId: currentUserEmail,
        comment: "",
        domain,
      }),
    );

    setShowNextStatusModal(false);
    setPendingNextStatus(null);
  };

  const handleCancelNextStatus = () => {
    setShowNextStatusModal(false);
    setPendingNextStatus(null);
  };

  const handleSplitButtonSelect = (selectedItem) => {
    handleNextStatus(selectedItem);
  };

  const handleSaveServiceDetails = async (changes) => {
    const requestId = Number(data?.requestId || data?.requestID);
    if (!requestId) return;

    // Guard: if nothing changed, skip the API call entirely.
    if (!changes || Object.keys(changes).length === 0) {
      setShowEditModal(false);
      return;
    }

    // Build payload with ONLY the dirty fields. Each updateX object is
    // omitted when unchanged so the backend won't write history rows.
    const payload = {
      requestId,
      actingUserEmail: userEmail,
      domain,
    };

    if ("natureOfEngagement" in changes) {
      payload.updateNatureOfEngagement = {
        value: changes.natureOfEngagement || null,
      };
    }
    if ("targetDeliveryDate" in changes) {
      payload.updateTargetDeliveryDate = {
        value: localDateInputToUtcIso(changes.targetDeliveryDate) || null,
      };
    }
    if ("accessReceivedDate" in changes) {
      payload.updateAccessReceivedDate = {
        value: localDateInputToUtcIso(changes.accessReceivedDate) || null,
      };
    }
    if ("allocationDate" in changes) {
      payload.updateAllocationDate = {
        value: localDateInputToUtcIso(changes.allocationDate) || null,
      };
    }
    if ("revisedStartDate" in changes) {
      payload.updateRevisedStartDate = {
        value: localDateInputToUtcIso(changes.revisedStartDate) || null,
      };
    }
    if ("revisedEndDate" in changes) {
      payload.updateRevisedEndDate = {
        value: localDateInputToUtcIso(changes.revisedEndDate) || null,
      };
    }
    if ("delegates" in changes) {
      payload.updateDelegates = {
        delegates: (changes.delegates || []).map((d) => ({
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          emailAddress: d.emailAddress || d.delegateEmail || "",
        })),
      };
    }

    const result = await dispatch(upsertServiceDetails(payload));
    if (!result.error) {
      setEditModalError("");
      setShowEditModal(false);
      showAutoToast("Service details saved successfully!", "success");
      if (requestId) {
        dispatch(
          fetchOrderDetails({
            requestId,
            domain,
            engagementManagerMailId: userEmail || null,
          }),
        );
      }
    } else {
      setEditModalError(
        extractBackendErrorMessage(
          result.payload,
          "Failed to save service details",
        ),
      );
    }
  };

  return (
    <div className="engagement-master-container">
      <div className="engagement-master-tabs">
        <div className="engagement-master-tab-section">
          <div className="tab-left-section">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
                role="tab"
              >
                <span className="tab-label motif-body1-default-regular">
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
          {isEngagementView && (
            <div className="tab-right-section">
              {activeTab === "service-details" &&
                (currentStageId == null || currentStageId <= 8) && (
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={isDisabled}
                    onClick={() => setShowEditModal(true)}
                  >
                    <span style={{ display: "flex", width: 12, height: 12 }}>
                      <EditIcon />
                    </span>
                    {t("view_order_page_editDetails")}
                  </Button>
                )}
              {currentStageId === 7 ? (
                <SplitButton
                  label={t("view_order_page_nextStatus")}
                  variant="secondary"
                  size="small"
                  disabled={isDisabled || isStatusUpdating}
                  dropDownList={[
                    "Second Level Review Completed",
                    "Request Completed",
                  ]}
                  onSelectItem={handleSplitButtonSelect}
                />
              ) : (
                <Button
                  size="small"
                  variant="secondary"
                  disabled={
                    isDisabled || isStatusUpdating || currentStageId === 9
                  }
                  onClick={handleDirectNextStatus}
                >
                  {t("view_order_page_nextStatus", {
                    defaultValue: "Next Status",
                  })}
                  <RightArrowIcon />
                </Button>
              )}
              <Button
                size="small"
                variant="secondary"
                disabled={isTerminal || isCurrentUserDelegate}
                onClick={() => setShowAssignModal(true)}
                style={isCurrentUserDelegate ? { display: "none" } : undefined}
              >
                {t("view_order_page_changeEngagementManager", {
                  defaultValue: "Change Engagement Manager",
                })}
              </Button>
              <MotifDropdown
                open={showMoreMenu}
                onClose={() => setShowMoreMenu(false)}
                trigger={
                  <button
                    type="button"
                    className="more-menu-trigger"
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    disabled={isDisabled}
                    aria-label="More actions"
                    aria-haspopup="true"
                    aria-expanded={showMoreMenu}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <circle cx="8" cy="3" r="1.5" />
                      <circle cx="8" cy="8" r="1.5" />
                      <circle cx="8" cy="13" r="1.5" />
                    </svg>
                  </button>
                }
              >
                <MotifDropdownItem
                  className={
                    isCancelServiceRequestDisabled
                      ? "engagement-master-more-menu-item--disabled"
                      : ""
                  }
                  disabled={isCancelServiceRequestDisabled}
                  aria-disabled={isCancelServiceRequestDisabled}
                  onClick={() => {
                    if (isCancelServiceRequestDisabled) return;
                    setShowMoreMenu(false);
                    handleOpenCancelOrderModal();
                  }}
                >
                  {t("view_order_page_cancelServiceRequest")}
                </MotifDropdownItem>
              </MotifDropdown>
            </div>
          )}
        </div>

        <div className="tabs-content">{renderTabContent()}</div>
      </div>

      <CancelOrderModal
        show={showCancelOrderModal}
        onClose={handleCloseCancelOrderModal}
        onConfirm={handleConfirmCancelOrder}
        orderId={data?.requestId}
      />

      <AssignEngagementManagerModal
        show={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignError("");
        }}
        onAssign={handleAssign}
        loading={assignLoading}
        error={assignError}
        onClearError={() => setAssignError("")}
        note={
          isAOA
            ? t("modal_assignEM_note", {
                defaultValue:
                  "Ensure that you assign requests to employees who are either Seniors or higher ups.",
              })
            : undefined
        }
      />

      <ConfirmationModal
        isModalVisible={showNextStatusModal}
        onClose={handleCancelNextStatus}
        onConfirm={handleConfirmNextStatus}
        modalHeaderText="Confirm Status Change"
        modalBodyText={
          pendingNextStatus?.type === "split"
            ? `Are you sure you want to move to "${pendingNextStatus.label}"?`
            : `Are you sure you want to move to "${getStageName(currentStageId + 1)}"?`
        }
        confirmButtonText="Confirm"
      />

      {String(domain).toLowerCase().includes("aoa") ? (
        <AOAEditServiceDetailsModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditModalError("");
          }}
          onSave={handleSaveServiceDetails}
          data={data}
          currentStageId={currentStageId}
          loading={upsertServiceDetailsLoading}
          error={editModalError}
          onClearError={() => setEditModalError("")}
          isCurrentUserDelegate={isCurrentUserDelegate}
        />
      ) : (
        <EditServiceDetailsModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditModalError("");
          }}
          onSave={handleSaveServiceDetails}
          data={data}
          currentStageId={currentStageId}
          isCurrentUserDelegate={isCurrentUserDelegate}
          loading={upsertServiceDetailsLoading}
          error={editModalError}
          onClearError={() => setEditModalError("")}
        />
      )}

      {showToast && (
        <ToastMessage
          variant={toastVariant}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default EngagementMaster;
